"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import { PageCompetitionSchema } from "@/sanity/pages/PageCompetitionTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { CTAButton } from "@/shadcn/ui/button";
import { format } from "date-fns";
import Image from "next/image";

export default function ScheduleSection({
  data,
  className,
}: {
  data: PageCompetitionSchema;
  className?: string;
}) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      day: format(date, "dd"),
      month: format(date, "MMM").toUpperCase(),
      fullDate: format(date, "dd.MM.yyyy"),
      time: format(date, "HH:mm:ss"),
    };
  };

  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="container flex flex-col items-center py-[9rem] lg:py-[21rem] relative">
        <div className="bg-white w-full lg:w-[min(180rem,100%)] border border-[#E5E5E5] flex flex-col gap-[3.62rem] lg:gap-[5.98rem] items-center pt-[4.5rem] pb-[6rem] px-[4.12rem] lg:py-[13.37rem]">
          <div className="w-full flex flex-col items-center justify-center gap-[1.5rem]">
            {data?.scheduleChip && (
              <ChipSerif text={data.scheduleChip} className="mb-0 leading-none" />
            )}

            {data?.scheduleTitle && (
              <div className="richtext text-center">
                <PortableText
                  value={data.scheduleTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-[2.75rem] w-full max-w-[140.375rem]">
            {/* Signup Stage */}
            <div className="flex-1 bg-[#F5F5F5] rounded-[2rem] p-[2.25rem] lg:py-[4rem] lg:px-[5.25rem] flex flex-col gap-[2.62rem] lg:gap-[3rem]">
              <h3 className="text-[2.25rem] lg:text-[3rem] leading-none font-[500] text-[#103037] text-center mb-0">
                {data?.signupStageTitle}
              </h3>

              <div className="relative flex flex-col gap-[2rem]">
                <div className="absolute left-[.8rem] top-1/2 -translate-y-1/2 w-[1px] h-[48%] bg-[#A8B0B8]" />

                {data?.signupEvents?.map((event, index) => {
                  const { day, month, fullDate, time } = formatDate(event.date);
                  return (
                    <div key={index} className="flex items-center gap-[2rem]">
                      <div className="p-[.25rem] rounded-full border border-[#103037]/40">
                        <svg className="w-[0.875rem] h-[0.875rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 7" fill="none">
                          <circle cx="3.5" cy="3.5" r="3.5" fill="#566578"/>
                        </svg>
                      </div>
                      <div className="flex-1 flex items-center justify-between bg-white rounded-[1.125rem] p-[1.5rem]">
                        <div className="flex items-center gap-[1.25rem]">                        
                          <div className="flex flex-col items-center justify-center gap-[.5rem] py-[1rem] px-[1.25rem] border border-[#755630]/10 rounded-[0.86175rem]">
                            <span className="text-[#F8362B] leading-none text-[1.7235rem] font-[600]">{month}</span>
                            <span className="text-[#393D41] leading-none text-[2.58525rem] font-[600]">{day}</span>
                          </div>
                          <div className="flex flex-col gap-[.5rem]">
                            <span className="text-[#393D41]/90 leading-none text-[2.15438rem] font-[500]">{fullDate}</span>
                            <span className="text-[#103037]/50 leading-none text-[2.15438rem]">{time}</span>
                          </div>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="29" viewBox="0 0 35 29" fill="none">
                          <g style={{ mixBlendMode: "plus-darker" }}>
                            <path d="M0 27.1825V0.944824H6.41271V27.1825H0Z" fill="#EBEBEB"/>
                            <path d="M28.2836 29L26.2925 26.5616C25.7544 26.8315 25.0727 27.0385 24.2655 27.1914C23.4584 27.3354 22.5615 27.4164 21.5749 27.4164C18.9739 27.4164 16.6959 26.8405 14.7586 25.6978C12.8213 24.5551 11.3235 22.9445 10.2652 20.875C9.20689 18.8055 8.67773 16.43 8.67773 13.7397C8.67773 11.0493 9.17102 8.79088 10.1486 6.72138C11.1262 4.65188 12.5881 3.01427 14.5164 1.80856C16.4447 0.602854 18.7946 0 21.5749 0C24.3552 0 26.6961 0.602854 28.6065 1.80856C30.5258 3.01427 31.9698 4.65188 32.9563 6.72138C33.9339 8.79088 34.4272 11.1303 34.4272 13.7397C34.4272 16.1781 34.0236 18.2926 33.2075 20.0742C32.4003 21.8647 31.2523 23.3674 29.7814 24.6001L33.3151 29H28.2656H28.2836ZM21.5839 22.1616C21.8529 22.1616 22.1041 22.1437 22.3193 22.1077C22.5435 22.0717 22.7498 22.0177 22.9471 21.9367L19.3775 17.5728H24.427L26.0503 19.7142C26.6154 19.1024 27.0549 18.3196 27.3777 17.3658C27.7006 16.421 27.8531 15.2063 27.8531 13.7217C27.8531 12.237 27.6378 10.8244 27.2073 9.54669C26.7768 8.269 26.1042 7.22526 25.1804 6.42445C24.2566 5.62364 23.0637 5.22774 21.5928 5.22774C20.122 5.22774 18.8843 5.63264 17.9605 6.42445C17.0367 7.22526 16.364 8.269 15.9335 9.54669C15.503 10.8244 15.2878 12.2191 15.2878 13.7217C15.2878 15.2243 15.512 16.583 15.9515 17.8607C16.3909 19.1384 17.0726 20.1731 17.9963 20.9649C18.9201 21.7567 20.113 22.1437 21.5839 22.1437V22.1616Z" fill="#EBEBEB"/>
                          </g>
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Competition Stage */}
            <div className="flex-1 bg-[#F5F5F5] rounded-[2rem] p-[2.25rem] lg:py-[4rem] lg:px-[5.25rem] flex flex-col gap-[2.62rem] lg:gap-[3rem]">
              <h3 className="text-[2.25rem] lg:text-[3rem] leading-none font-[500] text-[#103037] text-center mb-0">
                {data?.competitionStageTitle}
              </h3>

              <div className="relative flex flex-col gap-[2rem]">
                <div className="absolute left-[.8rem] top-1/2 -translate-y-1/2 w-[1px] h-[48%] bg-[#A8B0B8]" />

                {data?.competitionEvents?.map((event, index) => {
                  const { day, month, fullDate, time } = formatDate(event.date);
                  return (
                    <div key={index} className="flex items-center gap-[2rem]">
                      <div className="p-[.25rem] rounded-full border border-[#103037]/40">
                        <svg className="w-[0.875rem] h-[0.875rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7 7" fill="none">
                          <circle cx="3.5" cy="3.5" r="3.5" fill="#566578"/>
                        </svg>
                      </div>
                      <div className="flex-1 flex items-center justify-between bg-white rounded-[1.125rem] p-[1.5rem]">
                        <div className="flex items-center gap-[1.25rem]">
                          <div className="flex flex-col items-center justify-center gap-[.5rem] py-[1rem] px-[1.25rem] border border-[#755630]/10 rounded-[0.86175rem]">
                            <span className="text-[#F8362B] leading-none text-[1.7235rem] font-[600]">{month}</span>
                            <span className="text-[#393D41] leading-none text-[2.58525rem] font-[600]">{day}</span>
                          </div>
                          <div className="flex flex-col gap-[.5rem]">
                            <span className="text-[#393D41]/90 leading-none text-[2.15438rem] font-[500]">{fullDate}</span>
                            <span className="text-[#103037]/50 leading-none text-[2.15438rem]">{time}</span>
                          </div>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="29" viewBox="0 0 35 29" fill="none">
                          <g style={{ mixBlendMode: "plus-darker" }}>
                            <path d="M0 27.1825V0.944824H6.41271V27.1825H0Z" fill="#EBEBEB"/>
                            <path d="M28.2836 29L26.2925 26.5616C25.7544 26.8315 25.0727 27.0385 24.2655 27.1914C23.4584 27.3354 22.5615 27.4164 21.5749 27.4164C18.9739 27.4164 16.6959 26.8405 14.7586 25.6978C12.8213 24.5551 11.3235 22.9445 10.2652 20.875C9.20689 18.8055 8.67773 16.43 8.67773 13.7397C8.67773 11.0493 9.17102 8.79088 10.1486 6.72138C11.1262 4.65188 12.5881 3.01427 14.5164 1.80856C16.4447 0.602854 18.7946 0 21.5749 0C24.3552 0 26.6961 0.602854 28.6065 1.80856C30.5258 3.01427 31.9698 4.65188 32.9563 6.72138C33.9339 8.79088 34.4272 11.1303 34.4272 13.7397C34.4272 16.1781 34.0236 18.2926 33.2075 20.0742C32.4003 21.8647 31.2523 23.3674 29.7814 24.6001L33.3151 29H28.2656H28.2836ZM21.5839 22.1616C21.8529 22.1616 22.1041 22.1437 22.3193 22.1077C22.5435 22.0717 22.7498 22.0177 22.9471 21.9367L19.3775 17.5728H24.427L26.0503 19.7142C26.6154 19.1024 27.0549 18.3196 27.3777 17.3658C27.7006 16.421 27.8531 15.2063 27.8531 13.7217C27.8531 12.237 27.6378 10.8244 27.2073 9.54669C26.7768 8.269 26.1042 7.22526 25.1804 6.42445C24.2566 5.62364 23.0637 5.22774 21.5928 5.22774C20.122 5.22774 18.8843 5.63264 17.9605 6.42445C17.0367 7.22526 16.364 8.269 15.9335 9.54669C15.503 10.8244 15.2878 12.2191 15.2878 13.7217C15.2878 15.2243 15.512 16.583 15.9515 17.8607C16.3909 19.1384 17.0726 20.1731 17.9963 20.9649C18.9201 21.7567 20.113 22.1437 21.5839 22.1437V22.1616Z" fill="#EBEBEB"/>
                          </g>
                        </svg>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {data?.scheduleCta && (
            <CTAButton data={data.scheduleCta} />
          )}
        </div>
      </div>
    </section>
  );
}
